import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, Clock, ArrowLeft, Tag } from "lucide-react";
import { blogApi } from "@/lib/api";
import { useSeo, JsonLd } from "@/lib/seo";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    blogApi.get(slug)
      .then((data) => {
        setPost(data.post || null);
        setRelated(data.related || []);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useSeo({
    title: post ? `${post.meta_title || post.title} | Blog My Animation` : "Article | My Animation",
    description: post?.meta_description || post?.excerpt || "",
    path: `/blog/${slug}`,
    keywords: post?.tags?.join(", ") || "",
  });

  if (loading) {
    return (
      <section className="pt-40 pb-24 max-w-3xl mx-auto px-6">
        <p className="text-white/55 italic text-center">Chargement…</p>
      </section>
    );
  }

  if (notFound || !post) {
    return (
      <section className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center">
        <h2 className="font-serif-luxe text-4xl gold-text">Article introuvable</h2>
        <p className="text-white/65 mt-6">L'article que vous cherchez n'existe pas ou n'est plus publié.</p>
        <Link to="/blog" data-testid="blog-back-list" className="btn-ghost-gold mt-10 inline-flex">
          <ArrowLeft size={14} strokeWidth={1.4} /> Retour au blog
        </Link>
      </section>
    );
  }

  return (
    <>
      <JsonLd
        id="jsonld-blog-post"
        data={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.meta_description || post.excerpt,
          image: post.cover_image ? [post.cover_image] : undefined,
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          author: {
            "@type": "Organization",
            name: post.author || "My Animation",
            url: "https://myanimation.fr",
          },
          publisher: {
            "@type": "Organization",
            name: "My Animation",
            url: "https://myanimation.fr",
          },
          mainEntityOfPage: `https://myanimation.fr/blog/${post.slug}`,
          keywords: (post.tags || []).join(", "),
        }}
      />

      <article className="pt-32 md:pt-40 pb-20" data-testid="blog-post">
        {/* Hero */}
        <header className="max-w-3xl mx-auto px-6 md:px-10">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#DCB457] transition">
            <ArrowLeft size={12} strokeWidth={1.4} /> Tous les articles
          </Link>
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/30 px-2.5 py-1">
                  {t}
                </span>
              ))}
            </div>
          )}
          <h1 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl leading-tight mt-6">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-lg text-white/70 mt-6 leading-relaxed">{post.excerpt}</p>
          )}
          <div className="flex items-center gap-6 mt-8 text-[11px] uppercase tracking-[0.18em] text-white/55">
            {post.published_at && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={12} strokeWidth={1.4} /> {formatDate(post.published_at)}
              </span>
            )}
            {post.reading_time_min && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} strokeWidth={1.4} /> {post.reading_time_min} min de lecture
              </span>
            )}
          </div>
        </header>

        {/* Cover */}
        {post.cover_image && (
          <div className="max-w-5xl mx-auto px-6 md:px-10 mt-12">
            <div className="aspect-[16/8] overflow-hidden bg-black/50">
              <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-16 prose-blog" data-testid="blog-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post.content || ""}
          </ReactMarkdown>
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-20">
          <div className="card-luxe p-8 md:p-10 text-center">
            <p className="eyebrow">Envie d'aller plus loin ?</p>
            <h3 className="font-serif-luxe text-3xl mt-3">
              Parlons de votre <span className="italic gold-text">événement.</span>
            </h3>
            <p className="text-white/65 text-sm mt-3 max-w-md mx-auto">
              Devis gratuit, personnalisé et sans engagement.
            </p>
            <Link to="/contact" data-testid="blog-post-cta" className="btn-gold mt-7 inline-flex">
              Demander un devis
            </Link>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="max-w-5xl mx-auto px-6 md:px-10 mt-20">
            <p className="eyebrow">À lire aussi</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`blog-related-${r.slug}`}
                  className="card-luxe p-6 hover:border-[#DCB457]/45 transition"
                >
                  <h4 className="font-serif-luxe text-xl group-hover:text-[#DCB457]">{r.title}</h4>
                  {r.excerpt && <p className="text-sm text-white/60 mt-2 line-clamp-2">{r.excerpt}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
